import type { LucideIcon } from "lucide-react";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  CheckCircle2,
  Clock,
  Hand,
  Info,
  Lightbulb,
  Lock,
  MailCheck,
  MessageSquare,
  Reply,
  RotateCcw,
  Route,
  Send,
  ShieldCheck,
  Star,
  Truck,
  UserPlus,
  XCircle,
} from "lucide-react";
import type { ComplaintDetailResponse, ComplaintState } from "@/api/types";

/**
 * COMP-UX (D-132) — next-step-driven action hierarchy (W1/W2/W6/W8).
 *
 * 🔴 Production-rule #2: this is PRESENTATION over the backend-supplied
 * `allowedActions` booleans. The state config below only decides WHICH allowed
 * action is the primary CTA / secondary / overflow ordering + the business-language
 * label. Whether an action renders at all is the backend gate (state×role×flags) —
 * an action whose gate is false never appears, in any slot. RE-GROUP+RENAME ONLY:
 * every key maps 1:1 onto an existing modal handler; 0 new transitions.
 */

// Action keys = the non-null ModalKind set the drawer already wires.
export type ComplaintActionKey =
  | "submit"
  | "respond"
  | "awaiting"
  | "client-responded"
  | "resolve"
  | "cancel-proposal"
  | "approve"
  | "request-changes"
  | "apply"
  | "revoke"
  | "reject"
  | "escalate"
  | "de-escalate"
  | "close"
  | "reopen"
  | "severity"
  | "sla"
  | "assign"
  | "claim"
  | "return-queue"
  | "reroute"
  | "leave-unassigned"
  | "request-dept"
  | "prepare-supplier"
  | "add-supplier-response";

export interface FooterSlot {
  key: ComplaintActionKey;
  label: string;
  icon: LucideIcon;
  consequence?: string;
  danger?: boolean;
}

interface ActionDef {
  label: string;
  icon: LucideIcon;
  consequence?: string;
  danger?: boolean;
  gate: (c: ComplaintDetailResponse) => boolean;
}

const REGISTRY: Record<ComplaintActionKey, ActionDef> = {
  submit: { label: "Подати рекламацію", icon: Send, gate: (c) => c.allowedActions.canSubmit },
  respond: { label: "Відповідь клієнту", icon: Reply, gate: (c) => c.allowedActions.canRespond },
  awaiting: {
    label: "Очікувати відповідь клієнта",
    icon: Clock,
    gate: (c) => c.allowedActions.canMarkAwaitingClient,
  },
  "client-responded": {
    label: "Зафіксувати відповідь клієнта",
    icon: CheckCircle2,
    gate: (c) => c.allowedActions.canClientResponded,
  },
  resolve: { label: "Запропонувати рішення", icon: Lightbulb, gate: (c) => c.allowedActions.canPropose },
  "cancel-proposal": {
    label: "Скасувати пропозицію",
    icon: XCircle,
    gate: (c) => c.allowedActions.canCancelProposal,
  },
  approve: { label: "Погодити рішення", icon: ShieldCheck, gate: (c) => c.allowedActions.canApproveFinancial },
  "request-changes": {
    label: "Повернути на доопрацювання",
    icon: RotateCcw,
    gate: (c) => c.allowedActions.canRejectFinancial,
  },
  apply: { label: "Застосувати рішення", icon: CheckCircle2, gate: (c) => c.allowedActions.canApply },
  revoke: { label: "Відкликати погодження", icon: RotateCcw, gate: (c) => c.allowedActions.canRevokeApproval },
  reject: { label: "Відхилити", icon: XCircle, danger: true, gate: (c) => c.allowedActions.canReject },
  escalate: {
    label: "Передати керівнику",
    icon: ArrowUpCircle,
    consequence: "Керівник побачить її як ескальовану",
    gate: (c) => c.allowedActions.canEscalate,
  },
  "de-escalate": {
    label: "Повернути від керівника",
    icon: ArrowDownCircle,
    gate: (c) => c.allowedActions.canDeEscalate,
  },
  close: { label: "Закрити рекламацію", icon: Lock, gate: (c) => c.allowedActions.canClose },
  reopen: { label: "Відкрити повторно", icon: RotateCcw, gate: (c) => c.allowedActions.canReopen },
  severity: {
    label: "Змінити важливість",
    icon: Star,
    consequence: "Змінить важливість рекламації",
    gate: (c) => c.allowedActions.canChangeSeverity,
  },
  sla: {
    label: "Контрольні терміни",
    icon: Clock,
    consequence: "Дозволяє змінити дедлайн відповіді / вирішення",
    gate: (c) => c.allowedActions.canSlaOverride,
  },
  assign: {
    label: "Призначити відповідального",
    icon: UserPlus,
    consequence: "Рекламація перейде в роботу",
    gate: (c) => c.allowedActions.canAssign,
  },
  claim: { label: "Взяти з черги", icon: Hand, gate: (c) => c.allowedActions.canClaim },
  "return-queue": { label: "Повернути в чергу", icon: Route, gate: (c) => c.allowedActions.canReturnToQueue },
  reroute: {
    label: "Передати в чергу / відділ",
    icon: Route,
    consequence: "З'явиться в черзі служби якості / підтримки",
    gate: (c) => c.allowedActions.canReroute,
  },
  "leave-unassigned": {
    label: "Залишити без призначення",
    icon: Hand,
    consequence: "Рекламація лишиться без відповідального, видима в черзі",
    gate: (c) => c.allowedActions.canLeaveUnassigned,
  },
  "request-dept": {
    label: "Попросити інший відділ",
    icon: MessageSquare,
    consequence: "Створить запит у відділ; рекламація лишиться тут",
    // BS-4: cross-dept request is offered while the complaint is open.
    gate: (c) => !["closed", "rejected"].includes(c.currentState),
  },
  // D-133 C-W3 — supplier outreach (always overflow). Backend gate: product-related
  // + not draft/terminal + edit authority.
  "prepare-supplier": {
    label: "Підготувати звернення постачальнику",
    icon: Truck,
    consequence: "Сформує лист постачальнику з фото/відео-доказами",
    gate: (c) => c.allowedActions.canEmailSupplier,
  },
  // D-133 E-W2 — record the supplier's reply (always overflow). Backend gate:
  // supplierStatus sent|replied + still open + product-related + edit authority.
  "add-supplier-response": {
    label: "Додати відповідь постачальника",
    icon: MailCheck,
    consequence: "Зафіксує відповідь постачальника та оновить стадію",
    gate: (c) => c.allowedActions.canAddSupplierResponse,
  },
};

// Overflow priority order — any gated action not used as primary/secondary lands
// here (so no backend-allowed action is ever silently dropped → AC-REG).
const OVERFLOW_ORDER: ComplaintActionKey[] = [
  "claim",
  "return-queue",
  "reroute",
  "assign",
  "leave-unassigned",
  "awaiting",
  "respond",
  "resolve",
  "request-changes",
  "cancel-proposal",
  "revoke",
  "request-dept",
  "prepare-supplier",
  "add-supplier-response",
  "severity",
  "sla",
  "escalate",
  "de-escalate",
  "reopen",
  "reject",
];

interface StatePlan {
  primary: ComplaintActionKey | null;
  secondary?: ComplaintActionKey | null;
  /** read-only banner text (closed/rejected → no primary CTA, W3). */
  readOnly?: string;
}

function statePlan(c: ComplaintDetailResponse): StatePlan {
  switch (c.currentState) {
    case "draft":
      return { primary: "submit" };
    case "submitted":
      return { primary: "assign", secondary: "reroute" };
    case "in_progress":
      // W6 — backend-owned hasInitialResponse drives the primary CTA + labels.
      return c.hasInitialResponse
        ? { primary: "resolve", secondary: "respond" }
        : { primary: "respond", secondary: "resolve" };
    case "awaiting_client_response":
      return { primary: "client-responded", secondary: "respond" };
    case "pending_resolution_approval":
      return { primary: "approve", secondary: "request-changes" };
    case "resolution_approved":
      return { primary: "apply" };
    case "resolved":
      return { primary: "close" };
    case "escalated":
      return { primary: "de-escalate" };
    case "closed":
    case "rejected":
      return { primary: null, readOnly: "Перегляд лише для читання" };
    default:
      return { primary: null };
  }
}

/** Contextual label override (W6 + business phrasing per state). */
function labelFor(key: ComplaintActionKey, c: ComplaintDetailResponse): string {
  if (key === "respond") {
    if (c.currentState === "awaiting_client_response") return "Нагадати клієнту";
    if (c.currentState === "in_progress") {
      return c.hasInitialResponse ? "Оновити статус" : "Первинна відповідь";
    }
  }
  return REGISTRY[key].label;
}

function slot(key: ComplaintActionKey, c: ComplaintDetailResponse): FooterSlot {
  const def = REGISTRY[key];
  return { key, label: labelFor(key, c), icon: def.icon, consequence: def.consequence, danger: def.danger };
}

export interface FooterPlan {
  primary: FooterSlot | null;
  secondary: FooterSlot | null;
  overflow: FooterSlot[];
  readOnly?: string;
}

export function buildFooterPlan(c: ComplaintDetailResponse): FooterPlan {
  const plan = statePlan(c);
  const designated = new Set<ComplaintActionKey>();
  if (plan.primary) designated.add(plan.primary);
  if (plan.secondary) designated.add(plan.secondary);

  const primary = plan.primary && REGISTRY[plan.primary].gate(c) ? slot(plan.primary, c) : null;
  const secondary = plan.secondary && REGISTRY[plan.secondary].gate(c) ? slot(plan.secondary, c) : null;

  const overflow = OVERFLOW_ORDER.filter(
    (k) => !designated.has(k) && REGISTRY[k].gate(c),
  ).map((k) => slot(k, c));

  return { primary, secondary, overflow, readOnly: plan.readOnly };
}

// ── next-step banner (W1) ─────────────────────────────────────────────────────

export type BannerTone = "act" | "info" | "done";

export interface NextStepBanner {
  tone: BannerTone;
  icon: LucideIcon;
  title: string;
  desc: string;
}

const STATE_BANNER: Record<ComplaintState, NextStepBanner> = {
  draft: {
    tone: "info",
    icon: Info,
    title: "Чернетку ще не подано",
    desc: "Заповніть обов'язкові поля та подайте рекламацію в роботу.",
  },
  submitted: {
    tone: "act",
    icon: UserPlus,
    title: "Рекламація ще не має відповідального",
    desc: "Оберіть, хто буде її обробляти.",
  },
  in_progress: {
    tone: "act",
    icon: Reply,
    title: "Рекламація в роботі",
    desc: "Надайте первинну відповідь клієнту або запропонуйте рішення.",
  },
  awaiting_client_response: {
    tone: "act",
    icon: Clock,
    title: "Очікуємо відповідь клієнта",
    desc: "Коли клієнт відповів — позначте це, щоб продовжити роботу.",
  },
  pending_resolution_approval: {
    tone: "act",
    icon: ShieldCheck,
    title: "Рішення очікує погодження керівника",
    desc: "Перевірте запропоноване рішення та погодьте або поверніть на доопрацювання.",
  },
  resolution_approved: {
    tone: "done",
    icon: CheckCircle2,
    title: "Рішення погоджено",
    desc: "Застосуйте погоджене рішення по рекламації.",
  },
  resolved: {
    tone: "done",
    icon: CheckCircle2,
    title: "Питання вирішено",
    desc: "Закрийте рекламацію та вкажіть результат (замінено товар / компенсація / тощо).",
  },
  closed: {
    tone: "done",
    icon: Lock,
    title: "Рекламацію закрито",
    desc: "Перегляд лише для читання.",
  },
  rejected: {
    tone: "info",
    icon: XCircle,
    title: "Рекламацію відхилено",
    desc: "Перегляд лише для читання.",
  },
  escalated: {
    tone: "act",
    icon: ArrowUpCircle,
    title: "Рекламацію передано керівнику",
    desc: "Очікує рішення керівника. За потреби — поверніть від керівника.",
  },
};

export function buildNextStep(c: ComplaintDetailResponse): NextStepBanner {
  // The actionable next step for an orphan overrides the state banner regardless
  // of state — assigning a responsible is what unblocks it.
  if (c.assignmentStatus === "needs_manual_assignment" && !["closed", "rejected"].includes(c.currentState)) {
    return {
      tone: "act",
      icon: UserPlus,
      title: "Рекламація ще не має відповідального",
      desc: "Оберіть, хто буде її обробляти, або залиште в черзі.",
    };
  }
  return STATE_BANNER[c.currentState];
}
